import { z } from 'zod'
import {
  claimSpansOf,
  findHealthClaims,
  healthClaimSpans,
  removeClaims,
  removeHealthClaims,
  type ClaimMatcher,
} from './health-claims'
import type { SimulationPublic } from './schemas/simulation'

/**
 * 근거 없는 표현 규칙 (C45 제품 검토 리포트, 2026-10-30). 판매 문구 한 줄에서 '근거 없이 단정한 말'을 찾는 단일 원본이다.
 * - health: 효능·질병 표현. 목록은 health-claims.ts (로드맵 P8) 그대로 쓴다 — 생성·AI 수정 결과에서 걷어 내는 것과 같은 규칙
 * - certification: 인증·적합 단정 (HACCP 적합, FDA approved, 유기농 인증). 인증서를 받은 기록이 앱에 없다
 * - probability: 성공·판매 확률을 % 로 단정 (출시 성공 확률 80%). 그런 통계를 낼 자료가 없다
 * - taste_score: 맛·향·식감 점수 (단맛 8, 단맛 7.5, sweetness 8/10). 측정치나 관능 기준 없이 점수를 만들지 않는다
 *   (개발지시서 293행). 결과 화면의 AI 맛 추정 카드는 '추정' 꼬리표가 붙은 별도 칸이라 여기서 보지 않는다
 *
 * 오탐 기준: '100% 국산', '설탕 30% 줄임'(확률 말이 없다), '매운맛 3단계'·'단맛 20 g'(단위가 붙은 수),
 * 'HACCP 기준으로 관리'(단정 말이 없다)는 통과한다. 이 모듈은 앱 번들에도 들어가 lookbehind 를 쓰지 않는다.
 * 규칙을 바꾸면 UNSUPPORTED_CLAIM_RULES_VERSION 을 올린다 — 저장된 리포트에 그때 쓴 판이 남는다
 */
export const UNSUPPORTED_CLAIM_RULES_VERSION = 'claims-v1'
export const UNSUPPORTED_CLAIM_KINDS = [
  'health',
  'certification',
  'probability',
  'taste_score',
] as const
export type UnsupportedClaimKind = (typeof UNSUPPORTED_CLAIM_KINDS)[number]
export interface UnsupportedClaim {
  kind: UnsupportedClaimKind
  /** 글 안에서 걸린 그대로의 말 (NFKC 로 맞춘 뒤) */
  phrase: string
}

const NUMBER = '\\d+(?:\\.\\d+)?(?![\\d.])'

const CERT_MARKS =
  'HACCP|해썹|GMP|ISO\\s?22000|FSSC\\s?22000|식약처|유기농|친환경|할랄|코셔|FDA|USDA|organic|halal|kosher|有機|オーガニック|ハラール|有机|清真'
const CERT_ASSERTIONS =
  '적합|인증|획득|통과|승인|certified|compliant|approved|認証|認定|適合|取得|认证|認證|符合|通过|通過'

const PROBABILITY_WORDS_KO = '확률|성공률|가능성|승률'
const PROBABILITY_WORDS_EN = 'chance|chances|probability|likelihood|odds|success rate'
const PROBABILITY_WORDS_JA_ZH = '確率|成功率|可能性|概率|几率|機率'

const TASTE_WORDS_KO = '단맛|짠맛|신맛|산미|매운맛|쓴맛|감칠맛|풍미|향|식감|맛'
const TASTE_WORDS_EN =
  'sweetness|saltiness|sourness|acidity|bitterness|umami|spiciness|heat|aroma|flavou?r|texture|taste'
const TASTE_WORDS_JA = '甘味|甘さ|塩味|酸味|辛さ|苦味|旨味|うま味|香り|食感|味'
const TASTE_WORDS_ZH = '甜度|咸度|鹹度|酸度|辣度|苦味|鲜味|鮮味|香气|香氣|口感|味道'
/** 점수 뒤에 이 단위가 붙으면 점수가 아니라 양·단계·시간·온도다 (℃ 는 NFKC 로 °C 가 된다) */
const NOT_A_SCORE =
  '(?!\\s*(?:[%°]|단계|가지|종|개|도|분|시간|일|배|위|段階|種|度|日|级|級|种|(?:g|kg|mg|ml|l|steps?|levels?|ways?|kinds?|grams?|minutes?|min|hours?|days?|servings?|pieces?)(?![a-z])))'
const SCORE_TAIL = `(?:\\s*(?:/|out of)\\s*(?:5|10|100))?(?:\\s*(?:점|点|分|points?|pts))?`

const RULES: readonly { kind: Exclude<UnsupportedClaimKind, 'health'>; re: RegExp }[] = [
  {
    kind: 'certification',
    re: new RegExp(
      `(?:${CERT_MARKS})\\s?(?:을|를|에|이|가|の|を)?\\s?(?:${CERT_ASSERTIONS})`,
      'giu',
    ),
  },
  {
    kind: 'certification',
    re: /\b(?:certified|approved)\s+(?:by\s+(?:the\s+)?)?(?:HACCP|FDA|USDA|organic|halal|kosher)\b/giu,
  },
  { kind: 'certification', re: /\b(?:FDA|USDA)[\s-]approved\b/giu },
  {
    kind: 'probability',
    re: new RegExp(
      `(?:${PROBABILITY_WORDS_KO})\\s?(?:은|는|이|가|:)?\\s?(?:약\\s?)?${NUMBER}\\s?%|${NUMBER}\\s?%\\s?(?:의\\s?)?(?:${PROBABILITY_WORDS_KO})`,
      'giu',
    ),
  },
  {
    kind: 'probability',
    re: new RegExp(
      `\\b(?:${PROBABILITY_WORDS_EN})\\b(?:\\s+(?:of|is|at|to|for|about|around|over|succeed(?:ing)?|success|selling|launch)){0,4}\\s*:?\\s*${NUMBER}\\s?%|${NUMBER}\\s?%\\s+(?:${PROBABILITY_WORDS_EN})\\b`,
      'giu',
    ),
  },
  {
    kind: 'probability',
    re: new RegExp(
      `(?:${PROBABILITY_WORDS_JA_ZH})\\s?(?:は|が|:|：|为|為|是|约|約)?\\s?${NUMBER}\\s?%|${NUMBER}\\s?%\\s?(?:の|的)?(?:${PROBABILITY_WORDS_JA_ZH})`,
      'giu',
    ),
  },
  {
    kind: 'taste_score',
    re: new RegExp(
      `(?:${TASTE_WORDS_KO})\\s?(?:점수|평가)?\\s?(?:은|는|이|가|:)?\\s?${NUMBER}${NOT_A_SCORE}${SCORE_TAIL}`,
      'giu',
    ),
  },
  {
    kind: 'taste_score',
    re: new RegExp(
      `\\b(?:${TASTE_WORDS_EN})(?:\\s+(?:score|rating|level))?\\s*(?:of|is|:|=)?\\s*${NUMBER}${NOT_A_SCORE}${SCORE_TAIL}`,
      'giu',
    ),
  },
  {
    kind: 'taste_score',
    re: new RegExp(
      `(?:${TASTE_WORDS_JA}|${TASTE_WORDS_ZH})\\s?(?:スコア|評価|评分|評分|得分)?\\s?(?:は|:|：|为|為|是)?\\s?${NUMBER}${NOT_A_SCORE}${SCORE_TAIL}`,
      'giu',
    ),
  },
]

/** 글 안의 근거 없는 표현을 모두 찾는다 (없으면 빈 배열). 효능·질병 표현이 먼저 온다 */
export function findUnsupportedClaims(text: string | null | undefined): UnsupportedClaim[] {
  if (!text) return []
  const plain = text.normalize('NFKC')
  return [
    ...findHealthClaims(plain).map((phrase) => ({ kind: 'health' as const, phrase })),
    ...RULES.flatMap(({ kind, re }) =>
      [...plain.matchAll(re)].map((m) => ({ kind, phrase: m[0].trim() })),
    ),
  ]
}

/** 효능·질병 + 인증 단정·확률·맛 점수의 자리. 자동으로 뺄 때 쓴다 */
const allClaimSpans: ClaimMatcher = (plain) => [
  ...healthClaimSpans(plain),
  ...claimSpansOf(
    RULES.map((rule) => rule.re),
    plain,
  ),
]

/** 생성·AI 수정이 자동으로 걷어 내는 판매 문구 칸 (removeClaims 가 보는 칸과 같다) */
export const SALES_COPY_FIELDS = ['name', 'tagline', 'how_to_use', 'plain_step'] as const

/** 자동으로 뺀 표현 한 건. 서버 전용 기록(simulations.internal.removedClaims) — 앱 응답·AI 프롬프트에 싣지 않는다 */
export const removedClaimSchema = z.object({
  field: z.enum(SALES_COPY_FIELDS),
  kind: z.enum(UNSUPPORTED_CLAIM_KINDS),
  phrase: z.string().max(200),
  /** generation = 생성 확정, ai_edit = AI 수정 */
  at: z.enum(['generation', 'ai_edit']),
})
export type RemovedClaim = z.infer<typeof removedClaimSchema>
/** 제품마다 남기는 자동 제거 기록 수 (새 것을 남긴다) */
export const REMOVED_CLAIMS_KEEP = 20

/**
 * 판매 문구 자동 정리 (2026-10-30 대표 결정). 효능·질병 표현(P8)은 늘 걷어 낸다. autoRemoveUnsupported 면 인증 단정·확률·
 * 근거 없는 맛 점수도 같은 방식(걸린 문장만 빼고, 남는 게 없으면 중립 문구)으로 걷어 낸다 — 관리자 스위치
 * settings.unsupportedClaimsAutoRemove (기본 켬, 식품표시광고법 제8조 허위·과장 표시 방어). 무엇을 뺐는지 removed 로 돌려준다
 */
export function removeSalesCopyClaims<T extends SimulationPublic>(
  result: T,
  options: { autoRemoveUnsupported: boolean; at: RemovedClaim['at'] },
): { result: T; removed: RemovedClaim[] } {
  const cleaned = options.autoRemoveUnsupported
    ? removeClaims(result, allClaimSpans)
    : removeHealthClaims(result)
  if (cleaned === result) return { result, removed: [] }
  const fields: [RemovedClaim['field'], string, string][] = [
    ['name', result.product.name, cleaned.product.name],
    ['tagline', result.product.tagline, cleaned.product.tagline],
    ['how_to_use', result.product.howToUse, cleaned.product.howToUse],
    ...result.plainSteps.map((line, i): [RemovedClaim['field'], string, string] => [
      'plain_step',
      line,
      cleaned.plainSteps[i] ?? '',
    ]),
  ]
  const removed = fields.flatMap(([field, before, after]) =>
    before === after
      ? []
      : findUnsupportedClaims(before)
          .filter((claim) => options.autoRemoveUnsupported || claim.kind === 'health')
          .map((claim) => ({
            field,
            kind: claim.kind,
            phrase: claim.phrase.slice(0, 200),
            at: options.at,
          })),
  )
  return { result: cleaned, removed }
}

/** simulations.internal.removedClaims 읽기. 없거나 모양이 틀리면 빈 목록 */
export function readRemovedClaims(stored: unknown): RemovedClaim[] {
  return z
    .array(removedClaimSchema)
    .catch([])
    .parse(stored ?? [])
}

/** 지난 기록에 새 기록을 이어 붙여 최근 REMOVED_CLAIMS_KEEP 건만 남긴다 */
export function appendRemovedClaims(previous: unknown, removed: RemovedClaim[]): RemovedClaim[] {
  return [...readRemovedClaims(previous), ...removed].slice(-REMOVED_CLAIMS_KEEP)
}
